import { MutableRefObject, useEffect } from 'react';

interface ResizeObserverEntryWithHtmlElement extends ResizeObserverEntry {
  target: HTMLElement;
}

/**
 * Hook that registers a `ResizeObserver` to keep track of an element's
 * dimension changes. When dimensions changed, the passed `callback` is called.
 */
const useResizeObserver = (
  elementToObserve: HTMLElement | MutableRefObject<HTMLElement | null> | null,
  /**
   * The callback to call when a resize event happened.
   *   * **Important**: Make sure the callback isn't re-created on every render,
   *     e.g. by wrapping it in a `useCallback` to ensure the resize observer
   *     isn't re-created on every render.
   */
  callback: (resizeObserverEntry: ResizeObserverEntryWithHtmlElement) => void,
) => {
  useEffect(() => {
    if (!elementToObserve) {
      return;
    }

    let targetElement: HTMLElement;
    const isHtmlElement = elementToObserve instanceof HTMLElement;

    if (isHtmlElement) {
      targetElement = elementToObserve;
    } else {
      if (!elementToObserve.current) {
        return;
      }

      targetElement = elementToObserve.current;
    }

    const observer = new ResizeObserver(([entry]) => {
      callback(entry as ResizeObserverEntryWithHtmlElement);
    });

    observer.observe(targetElement);

    return () => {
      observer.disconnect();
    };
  }, [callback, elementToObserve]);
};

export default useResizeObserver;
